import { z } from 'zod';

export const getTagsQuerySchema = z.object({
  search: z.string().optional(),
  categoryType: z.string().optional(),
  campaignId: z.string().optional(),
  userId: z.string().optional(),
  name: z.string().optional(),
  mediaPlanId: z.string().optional(),
});

export const getTreeTagsQuerySchema = z.object({
  hierarchyName: z.string().optional(),
  tagNameSearch: z.string().optional(),
});

export const getOldTagsQuerySchema = z.object({
  type: z.string().optional(),
});

export const TagType = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  industry: z.number().nullable().optional(),
  parent: z.string().nullable().optional(),
  avatar: z.string().nullable().optional(),
  usersCount: z.number(),
});

export const OldTagType = z.object({
  id: z.number(),
  name: z.string(),
  category: z.number().optional(),
  industry: z.number().nullable().optional(),
  parent: z.number().nullable().optional(),
  avatar: z.string().nullable().optional(),
  usersCount: z.number().optional(),
});

export const CreationTagType = z.object({
  id: z.string().optional(),
  name: z.string(),
  category: z.string().optional(),
  industry: z.number().nullable().optional(),
  parent: z.string().nullable().optional(),
  avatar: z.string().nullable().optional(),
});

const TagChildrenTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  industry: z.number().nullable(),
  avatar: z.string().nullable().optional(),
  get childTag() {
    return z.array(TagChildrenTypeSchema).nullable().optional();
  },
  campaigns: z.array(z.string()).nullable(),
  users: z.array(z.number()).nullable(),
  connectedEntities: z.any().nullish(),
});

export const ExtendedTagType = z.object({
  id: z.string(),
  name: z.string(),
  industry: z.number().nullable(),
  avatar: z.string().nullable().optional(),
  childTag: z.array(TagChildrenTypeSchema).nullable(),
  campaigns: z.array(z.string()).nullable(),
  users: z.array(z.number()).nullable(),
  parentTag: TagChildrenTypeSchema.nullable(),
  connectedEntities: z.any().nullish(),
});

export const UpdateTagType = z.object({
  name: z.string().optional(),
  users: z.array(z.number()).nullable().optional(),
  campaigns: z.array(z.string()).nullable().optional(),
  connectedEntities: z.array(z.number()).nullable().optional(),
});

const MediaPlanSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  dateFrom: z.string(),
  dateTo: z.string(),
});

const UserSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  role: z.string(),
  avatar: z.string().nullable(),
});

const CategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  level: z.number(),
});

const TagSchema = z.object({
  id: z.string(),
  name: z.string(),
  categoryName: z.string(),
  mediaPlans: z.array(MediaPlanSchema).nullable().optional(),
  users: z.array(UserSchema).nullable().optional(),
  get children() {
    return z.array(TagSchema).nullable().optional();
  },
  userHasAccess: z.boolean().optional(),
  assignedToMediaPlan: z.boolean().optional(),
  assignedMediaPlansInBranch: z
    .record(z.string(), z.array(z.string()))
    .nullable()
    .optional(),
});

const RootValueSchema = z.object({
  id: z.string(),
  name: z.string(),
  categories: z.array(CategorySchema),
  tags: z.array(TagSchema),
});

export const getTreeDataResponseSchema = z.array(
  z.record(z.string(), RootValueSchema),
);

export const getTagsResponseSchema = z.array(TagType);
export const getOldTagsResponseSchema = z.array(OldTagType);
export const getTagResponseSchema = ExtendedTagType;
export const createTagRequestSchema = z.instanceof(FormData);
export const updateTagRequestSchema = UpdateTagType;
export const createTagResponseSchema = CreationTagType;
export const deleteTagResponseSchema = z.string();

export const copyUsersRequestSchema = z.object({
  source_tag_id: z.string(),
  target_tag_id: z.string(),
});
export const copyUserResponseSchema = z.string();

export type TagBffType = z.infer<typeof TagType>;
export type TagType = z.infer<typeof TagSchema>;
export type TreeData = z.infer<typeof getTreeDataResponseSchema>;
export type MediaPlanType = z.infer<typeof MediaPlanSchema>;
export type SimplifiedCategoryType = z.infer<typeof CategorySchema>;
export type UpdateTagBffType = z.infer<typeof UpdateTagType>;

export const querySchema = z.object({
  cache: z.string(),
});

export const mediaPlanAssignTagsResponseSchema = z.string();
export const mediaPlanAssignTagsRequestSchema = z.object({
  campaign: z.string(),
  tags: z.array(z.string()),
});

export const mediaPlanDismissTagsResponseSchema = z.string();
export const mediaPlanDismissTagsRequestSchema = z.object({
  campaign: z.string(),
  tags: z.array(z.string()),
});
